import { Injectable } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormArray,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import _ from 'lodash';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { UserPlanning } from 'src/app/classes/user-planning';
import { environment } from '../../environments/environment';
import { Entity } from '../classes/entity.class';
import { PlanningFile } from '../classes/planning-file';
import { PlanningHasEntity } from '../classes/planning-has-entity.class';
import { PlanningMaterial } from '../classes/planning-material.class';
import { Planning } from '../classes/planning.class';
import { Status } from '../classes/status.class';
import { LocalStorage } from '../storage.class';
import { Utils } from '../utils.class';
import { localEntityIsAvailableValidator } from '../validators/local-entity-is-available.validator';
import { localUserIsAvailableValidator } from '../validators/local-user-is-available.validator';
import { ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';
import { EntitiesService } from './entities/entities.service';
import { EntityField, EntityType } from './entities/entity-type.class';
import { PlanningFixedService } from './planning/planning-fixed.service';
import { PlanningHasService } from './planning/planning-has.service';
import { StatusService } from './planning/status.service';
import { UserPlanningService } from './planning/user-planning.service';

@Injectable({
    providedIn: 'root',
})
export class EntityTypeService {
    constructor(
        private apiService: ApiService,
        private statusService: StatusService,
        private planningFixedService: PlanningFixedService,
        private userPlanningService: UserPlanningService,
        private planningHasService: PlanningHasService,
        private entitiesService: EntitiesService
    ) {}

    writeEntityTypesToDb() {
        if (!environment.production) {
            this.apiService.postCall$<any>('entitytypes/import', { entityTypes: environment.entitytypes }).subscribe();
        }
    }

    getTableHeaders(entityType: EntityType): EntityField[] {
        const headers = [];

        [entityType.fields_phe, entityType.fields_phe_data].forEach(entityFields => {
            Object.entries(entityFields).forEach(([fcKey, entityField]) => {
                entityField.key = fcKey;
                headers.push(entityField);
            });
        });

        return headers.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    getFormFromEntityType(entityType: EntityType, entity: Entity, subscriptions: Subscription): PlanningFormData {
        const planningForm = this.createPlanningFormGroup(entityType);
        const formData = new PlanningFormData();
        formData.form = planningForm.form;
        formData.planningFieldsMap = planningForm.planningFieldsMap;
        formData.planningFiles = [];
        this.addPheToForm(formData, entityType, entity, true, undefined, subscriptions);

        this.performerChange(subscriptions, formData, entityType);
        return this.buildConditionalValidators(formData, subscriptions);
    }

    getFormFromPlanning(planning: Planning, subscriptions: Subscription, copy = false, entityTypeKey = null) {
        const mainEntityType = environment.entitytypes[entityTypeKey ?? planning.mainPlanning.entitytype_key];
        if (copy && entityTypeKey) {
            planning = structuredClone(planning);
            planning.mainPlanning.entitytype_key = entityTypeKey;
            planning.planning_has.find(ph => ph.id === planning.planning_has_id).entitytype_key = entityTypeKey;
        }
        const planningForm = this.createPlanningFormGroup(mainEntityType, planning);

        const formData = new PlanningFormData();

        formData.orginal = copy ? null : _.cloneDeep(planning);
        if (copy) {
            formData.orginal = new Planning();
            formData.orginal.user_planning = [];

            planning.user_planning.forEach(up => {
                const cup = { ...up };

                delete cup.id;
                delete cup.updated_at;

                formData.orginal.user_planning.push(cup);
            });
        }

        formData.form = planningForm.form;
        formData.planningFieldsMap = planningForm.planningFieldsMap;
        formData.planningFiles = [
            ...(planning.planning_files.map(pf => {
                if (copy) {
                    pf = structuredClone(pf);
                    delete pf.id;
                    return pf;
                }
                return pf;
            }) ?? []),
        ];

        if (mainEntityType.fields_planning) {
            Object.entries(mainEntityType.fields_planning).forEach(([key, entityField]) => {
                if (planning[key] !== undefined) {
                    formData.form.controls.planning.get(key).setValue(planning[key]);
                }

                entityField.extraFields?.forEach(hiddenField => {
                    formData.form.controls.planning.get(hiddenField).setValue(planning[hiddenField]);
                });
            });
        }

        if (mainEntityType.fields_planning_data) {
            Object.entries(mainEntityType.fields_planning_data).forEach(([key]) => {
                formData.form.controls.planningData.get(key).setValue(planning.data ? planning.data[key] : null);
            });
        }

        planning.planning_has.forEach(phe => {
            const entityType = environment.entitytypes[phe.entitytype_key];
            const pheForm = this.addPheToForm(
                formData,
                entityType,
                planning.mainPlanning.entity,
                planning.planning_has_id === phe.id,
                phe.id,
                subscriptions
            );

            pheForm.pheFields.id = phe.id;
            pheForm.pheFields.orginal = copy ? null : phe;
            pheForm.pheFields.form = pheForm.form;

            if (entityType.fields_phe) {
                Object.entries(entityType.fields_phe).forEach(([key, entityField]) => {
                    pheForm.form
                        .get('phe')
                        .get(key)
                        .setValue(phe[key] ?? null);

                    entityField.extraFields?.forEach(hiddenField => {
                        pheForm.form.get('phe').get(hiddenField).setValue(phe[hiddenField]);
                    });
                });
            }

            if (entityType.fields_phe_data) {
                const pheData = phe.data ?? {};

                Object.entries(entityType.fields_phe_data).forEach(([key]) => {
                    pheForm.form
                        .get('pheData')
                        .get(key)
                        .setValue(pheData[key] ?? null);
                });
            }
        });

        this.performerChange(subscriptions, formData, mainEntityType);

        return this.buildConditionalValidators(formData, subscriptions);
    }

    getPlanningObject(planningFormData: PlanningFormData) {
        let planning = planningFormData.orginal ? structuredClone(planningFormData.orginal) : new Planning();

        Object.assign(planning, planningFormData.form.value.planning);

        planning.data = {};

        Object.assign(planning.data, planningFormData.form.value.planningData);

        const planning_has = [];

        planningFormData.pheFields.forEach(pheData => {
            const phe = planning.planning_has?.find(p => p.id === pheData.id && p.id) ?? new PlanningHasEntity();

            Object.assign(phe, pheData.form.value.phe);

            phe.data = {};

            Object.assign(phe.data, pheData.form.value.pheData);

            planning_has.push(phe);
        });

        planning.planning_has = planning_has;

        planning.user_planning = planningFormData.form.controls.employeePlanning.value as UserPlanning[];
        planning.planning_materials = planningFormData.form.controls.planningMaterials.value as PlanningMaterial[];

        // Process planning materials if present
        if (planningFormData.form.contains('planningMaterials')) {
            const materialsFormArray = planningFormData.form.get('planningMaterials') as any as FormArray;

            planning.planning_materials = materialsFormArray.value.map(
                (pm: PlanningMaterial & { raw_material_ids: number[] }) => ({
                    id: pm.id,
                    material_id: pm.material_id,
                    raw_material_ids: pm.raw_material_ids,
                    quantity: pm.quantity,
                })
            );
        }

        planning.planning_files = [...(planningFormData.planningFiles ?? [])];

        return planning;
    }

    addPheToForm(
        formData: PlanningFormData,
        entityType: EntityType,
        mainEntity?: Entity,
        isMainPlanning = false,
        ownId?: number,
        subscriptions?: Subscription
    ) {
        const pheForm = this.createPheFormGroup(entityType, formData?.orginal);

        if (!pheForm.form.controls.phe.get('entitytype_key')) {
            pheForm.form.controls.phe.addControl('entitytype_key', new FormControl(entityType.key));

            if (isMainPlanning) {
                pheForm.form.controls.phe.addControl('_isMainPlanning', new FormControl(isMainPlanning));
                pheForm.form.controls.phe.addControl('entity_id', new FormControl(mainEntity.id));

                formData.form.controls.mainPlanningHas = pheForm.form;
                formData.mainPhe = pheForm.pheFields;

                const beginDateChange$ = pheForm.form.controls.phe.get('begindate').valueChanges;
                const endDateChange$ = pheForm.form.controls.phe.get('enddate').valueChanges;

                subscriptions.add(
                    beginDateChange$.subscribe(begindate => {
                        const mainBegin = new Date(pheForm.form.value.phe['begindate']);
                        formData.form.controls.planningHas.controls.forEach(pheFg => {
                            if (pheFg !== pheForm.form) {
                                const pheBegin = new Date(pheFg.get('phe').get('begindate').value);
                                if (Utils.getTimeOrNull(mainBegin) === Utils.getTimeOrNull(pheBegin)) {
                                    pheFg.get('phe').get('begindate').setValue(new Date(begindate));
                                    pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                } else if (
                                    Utils.formatDate(mainBegin) === Utils.formatDate(pheBegin) &&
                                    Utils.formatDate(new Date(begindate)) !== Utils.formatDate(pheBegin)
                                ) {
                                    pheFg
                                        .get('phe')
                                        .get('begindate')
                                        .setValue(Utils.setDate(new Date(begindate), pheBegin));
                                    pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                }
                            }
                        });
                    })
                );
                subscriptions.add(
                    endDateChange$.subscribe(enddate => {
                        const mainEnd = new Date(pheForm.form.value.phe['enddate']);
                        formData.form.controls.planningHas.controls.forEach(pheFg => {
                            if (pheFg !== pheForm.form) {
                                const pheEnd = new Date(pheFg.get('phe').get('enddate').value);
                                if (Utils.getTimeOrNull(mainEnd) === Utils.getTimeOrNull(pheEnd)) {
                                    pheFg.get('phe').get('enddate').setValue(new Date(enddate));
                                    pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                } else if (
                                    Utils.formatDate(mainEnd) === Utils.formatDate(pheEnd) &&
                                    Utils.formatDate(new Date(enddate)) !== Utils.formatDate(pheEnd)
                                ) {
                                    pheFg
                                        .get('phe')
                                        .get('enddate')
                                        .setValue(Utils.setDate(new Date(enddate), pheEnd));
                                    pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                }
                            }
                        });
                    })
                );
            } else if (formData.mainPhe?.phe) {
                (pheForm.form.controls.phe.get('begindate') as any as FormControl).setValue(
                    formData.mainPhe.phe.get('begindate').fc.value
                );
                (pheForm.form.controls.phe.get('enddate') as any as FormControl).setValue(
                    formData.mainPhe.phe.get('enddate').fc.value
                );
            }
        }

        if (pheForm.form.controls.phe.get('driver_user_id')) {
            subscriptions?.add(
                pheForm.form.controls.phe.get('driver_user_id').valueChanges.subscribe(value => {
                    formData.form.controls.employeePlanning.controls.forEach(employeePlanningControl => {
                        employeePlanningControl.updateValueAndValidity();
                    });
                })
            );
        }

        if (pheForm.form.controls.phe.get('begindate') && pheForm.form.controls.phe.get('enddate')) {
            subscriptions?.add(
                pheForm.form.controls.phe.get('begindate').valueChanges.subscribe(value => {
                    if (value && pheForm.form.controls.phe.get('enddate').value) {
                        const newDate = new Date(value);
                        const newEndDate = new Date(pheForm.form.controls.phe.get('enddate').value);

                        newEndDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());

                        setTimeout(() => {
                            if (newEndDate.getTime() > newDate.getTime()) {
                                if (
                                    Utils.getTimeOrNull(pheForm.form.controls.phe.get('enddate').value) !==
                                    Utils.getTimeOrNull(newEndDate)
                                ) {
                                    (pheForm.form.controls.phe.get('enddate') as any as FormControl).setValue(
                                        newEndDate
                                    );
                                }
                            } else {
                                newEndDate.setDate(newDate.getDate() + 1);
                                if (
                                    Utils.getTimeOrNull(pheForm.form.controls.phe.get('enddate').value) !==
                                    Utils.getTimeOrNull(newEndDate)
                                ) {
                                    (pheForm.form.controls.phe.get('enddate') as any as FormControl).setValue(
                                        newEndDate
                                    );
                                }
                            }
                        }, 0);
                    }
                })
            );
        }

        formData.form.controls.planningHas.push(pheForm.form);

        formData.pheFields.push(pheForm.pheFields);

        pheForm.pheFields.form = pheForm.form;

        return pheForm;
    }

    removePheFromForm(formData: PlanningFormData, pheField: PheFormData) {
        const indexToRemove = formData.form.controls.planningHas.value.findIndex(
            ({ phe }) =>
                phe.entity_id === pheField.form.value.phe.entity_id &&
                phe.driver_user_id === pheField.form.value.phe.driver_user_id
        );

        formData.form.controls.planningHas.removeAt(indexToRemove);
        formData.form.controls.planningHas.value.splice(indexToRemove, 1);

        formData.pheFields.splice(indexToRemove, 1);

        const orginalIndex = formData.orginal?.planning_has.findIndex(ph => ph.id === pheField.id);

        if (orginalIndex >= 0) {
            formData.orginal.planning_has.splice(orginalIndex, 1);
        }

        formData.form.controls.planningHas.updateValueAndValidity();
        formData.form.controls.planningHas.markAsDirty();

        if (pheField.form.value.phe.driver_user_id) {
            formData.form.controls.employeePlanning.controls.forEach(employeePlanningControl => {
                employeePlanningControl.updateValueAndValidity();
            });
        }
    }

    private performerChange(
        subscriptions: Subscription,
        formData: PlanningFormData,
        entityType: EntityType,
        field = 'performer'
    ) {
        subscriptions.add(
            combineLatest([
                formData.form.controls.planning
                    .get(field)
                    .valueChanges.pipe(startWith(formData.form.controls.planning.get(field).value)),
                this.entitiesService.getByType(entityType.has_entitytype_keys.map(key => key)),
            ]).subscribe(([newPerformerId, entities]) => {
                const previousPerformerId = formData.form.controls.planning.value[field];
                if (previousPerformerId === newPerformerId) {
                    return;
                }

                // Clean up previous performer's PHE if it exists
                if (previousPerformerId) {
                    const previousDriverEntity = entities.find(e => e.driver_user_id === previousPerformerId);
                    if (previousDriverEntity) {
                        const pheToRemove = formData.pheFields.find(
                            phe =>
                                phe.form.value.phe.entity_id === previousDriverEntity.id &&
                                phe.form.value.phe.driver_user_id === previousPerformerId
                        );
                        if (pheToRemove) {
                            this.removePheFromForm(formData, pheToRemove);
                        }
                    }
                }

                // Handle new performer
                if (newPerformerId) {
                    const driverFrom = entities.find(e => e.driver_user_id === newPerformerId);
                    if (driverFrom) {
                        const existingPheWithDriver = formData.pheFields.some(
                            phe => phe.form.value.phe.driver_user_id === newPerformerId
                        );
                        if (!existingPheWithDriver) {
                            const existingPheWithoutDriver = formData.pheFields.find(
                                phe =>
                                    phe.form.value.phe.entity_id === driverFrom.id && !phe.form.value.phe.driver_user_id
                            );

                            if (existingPheWithoutDriver) {
                                (
                                    existingPheWithoutDriver.form.controls.phe.get(
                                        'driver_user_id'
                                    ) as any as FormControl
                                ).setValue(newPerformerId);
                            } else {
                                const phe = this.addPheToForm(
                                    formData,
                                    driverFrom.entitytypes[0],
                                    undefined,
                                    false,
                                    undefined,
                                    subscriptions
                                );
                                (phe.form.controls.phe.get('driver_user_id') as any as FormControl).setValue(
                                    newPerformerId
                                );
                                (phe.form.controls.phe.get('entity_id') as any as FormControl).setValue(driverFrom.id);
                            }
                        }
                    }
                }
            })
        );
    }

    private createPlanningFormGroup(entityType: EntityType, planning?: Planning) {
        const planningFieldsMap = new Map<string, Map<string, EntityFormField>>();

        const form = new FormGroup<PlanningFg>({
            planning: new FormGroup({}),
            planningData: new FormGroup({}),
            planningHas: new FormArray([]),
            mainPlanningHas: new FormGroup({
                phe: new FormGroup({}),
                pheData: new FormGroup({}),
            }),
            employeePlanning: new FormArray([]),
            planningMaterials: new FormArray([]),
        });

        Object.entries({
            planning: entityType.fields_planning,
            planningData: entityType.fields_planning_data,
        }).forEach(([formGroup, entityData]) => {
            planningFieldsMap.set(formGroup, new Map<string, EntityFormField>());

            if (entityData) {
                Object.entries(entityData).forEach(([fcKey, entityField]) => {
                    const control = new FormControl(entityField.default ?? null);

                    (form.get(formGroup) as FormGroup).addControl(fcKey, control);

                    control.addValidators(this.buildValidators(entityField));

                    // Add async validators
                    const asyncValidators = this.buildAsyncValidators(
                        entityField,
                        form.get(formGroup) as FormGroup,
                        planning
                    );

                    if (asyncValidators.length > 0) {
                        control.addAsyncValidators(asyncValidators);
                    }

                    planningFieldsMap.get(formGroup).set(fcKey, { ...entityField, fc: control });

                    entityField.extraFields?.forEach(hiddenField => {
                        (form.get(formGroup) as FormGroup).addControl(hiddenField, new FormControl());
                    });
                });
            }
        });

        return { form, planningFieldsMap };
    }

    private createPheFormGroup(entityType: EntityType, planning?: Planning) {
        const pheFields = new PheFormData();

        const form = new FormGroup({
            phe: new FormGroup({}),
            pheData: new FormGroup({}),
        });

        Object.entries({
            phe: entityType.fields_phe,
            pheData: entityType.fields_phe_data,
        }).forEach(([formGroup, entityData]) => {
            pheFields[formGroup] = new Map<string, EntityFormField>();

            if (entityData) {
                Object.entries(entityData).forEach(([fcKey, entityField]) => {
                    const control = new FormControl(entityField.default ?? null);

                    (form.get(formGroup) as FormGroup).addControl(fcKey, control);

                    control.addValidators(this.buildValidators(entityField));

                    // Add async validators
                    const asyncValidators = this.buildAsyncValidators(
                        entityField,
                        form.get(formGroup) as FormGroup,
                        planning
                    );

                    if (asyncValidators.length > 0) {
                        control.addAsyncValidators(asyncValidators);
                    }

                    pheFields[formGroup].set(fcKey, { ...entityField, fc: control });

                    entityField.extraFields?.forEach(hiddenField => {
                        (form.get(formGroup) as FormGroup).addControl(hiddenField, new FormControl());
                    });
                });
            }
        });

        return { form, pheFields };
    }

    private buildValidators(field: EntityField): ValidatorFn[] {
        const validators: ValidatorFn[] = [];
        if (field.required) {
            validators.push(Validators.required);
        }
        if (field.validators?.min) {
            validators.push(Validators.min(field.validators.min));
        }
        if (field.validators?.max) {
            validators.push(Validators.max(field.validators.max));
        }
        if (field.validators?.pattern) {
            validators.push(Validators.pattern(field.validators.pattern));
        }
        if (field.validators?.minLength) {
            validators.push(Validators.minLength(field.validators.minLength));
        }
        if (field.validators?.maxLength) {
            validators.push(Validators.maxLength(field.validators.maxLength));
        }

        return validators;
    }

    private buildAsyncValidators(field: EntityField, formGroup: FormGroup, planning?: Planning): AsyncValidatorFn[] {
        const asyncValidators: AsyncValidatorFn[] = [];

        if (field.asyncValidators) {
            field.asyncValidators.forEach(asyncValidatorConfig => {
                switch (asyncValidatorConfig.type) {
                    case 'localUserIsAvailable':
                        asyncValidators.push(
                            localUserIsAvailableValidator({
                                formGroup,
                                planningId: planning?.id,
                                userPlanningService: this.userPlanningService,
                                planningHasService: this.planningHasService,
                                ...asyncValidatorConfig.config,
                            })
                        );
                        break;
                    case 'localEntityIsAvailable':
                        asyncValidators.push(
                            localEntityIsAvailableValidator({
                                formGroup,
                                planningId: planning?.id,
                                planningHasService: this.planningHasService,
                                entitiesService: this.entitiesService,
                                ...asyncValidatorConfig.config,
                            })
                        );
                        break;
                    // Add more async validators here as needed
                    default:
                        console.warn(`Unknown async validator type: ${asyncValidatorConfig.type}`);
                }
            });
        }

        return asyncValidators;
    }

    private buildConditionalValidators(planningForm: PlanningFormData, subscriptions: Subscription): PlanningFormData {
        // planning fixed validator
        const statusControl = planningForm.form.controls.planning.get('status_key');
        const begindateControl = planningForm.form.controls.mainPlanningHas.get('phe').get('begindate');

        const statusList$ = this.statusService.getList('planning').pipe(shareReplay(1));

        const statusValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
            const statusKey = control.value;
            const begindateValue = begindateControl.value;

            if (!begindateValue || !statusKey) {
                return of(null);
            }
            const begindate = new Date(begindateValue);

            return this.planningFixedService.getLatestItem().pipe(
                take(1),
                switchMap(fixed => {
                    const isFixed = fixed && new Date(fixed.fixeduntil).getTime() >= begindate.getTime();
                    if (!isFixed) {
                        return of(null);
                    }

                    return statusList$.pipe(
                        take(1),
                        map((statuses: Status[]) => {
                            const selectedStatus = statuses.find(s => s.key === statusKey);
                            if (selectedStatus && !selectedStatus.fixed) {
                                return { invalidStatusForFixed: { statusName: selectedStatus.name } };
                            }
                            return null;
                        })
                    );
                })
            );
        };

        statusControl.setAsyncValidators(statusValidator);
        statusControl.updateValueAndValidity({ emitEvent: false });

        subscriptions.add(
            begindateControl.valueChanges.subscribe(() => {
                statusControl.updateValueAndValidity({ emitEvent: false });
            })
        );

        // Check role-based permissions BEFORE setting up disable rules
        // This ensures role-based disabling always takes precedence
        this.checkAndDisableFormBasedOnRoles(planningForm);

        // Set up conditional validators for planning fields
        for (const [group, fields] of planningForm.planningFieldsMap) {
            for (const [key, entityField] of fields) {
                if (entityField.requiredRules) {
                    const control = planningForm.form.get(group).get(key) as FormControl;
                    for (const rule of entityField.requiredRules) {
                        this.setupRule(
                            control,
                            entityField,
                            rule,
                            planningForm.form,
                            ['planning', 'planningData'],
                            subscriptions
                        );
                    }
                }
                if (entityField.disableRules) {
                    for (const rule of entityField.disableRules) {
                        this.setupDisableRule(rule, planningForm, ['planning', 'planningData'], subscriptions);
                    }
                }
                if (entityField.atLeastOneRequiredRules) {
                    for (const rule of entityField.atLeastOneRequiredRules) {
                        this.setupAtLeastOneRequiredRule(
                            rule,
                            planningForm.form,
                            ['planning', 'planningData'],
                            planningForm.pheFields,
                            subscriptions
                        );
                    }
                }
            }
        }

        // Set up conditional validators for PHE fields
        planningForm.pheFields.forEach(phe => {
            for (const [group, fields] of Object.entries({ phe: phe.phe, pheData: phe.pheData })) {
                for (const [key, entityField] of fields) {
                    if (entityField.requiredRules) {
                        const control = phe.form.get(group).get(key) as FormControl;
                        for (const rule of entityField.requiredRules) {
                            this.setupRule(
                                control,
                                entityField,
                                rule,
                                planningForm.form,
                                ['planning', 'planningData'],
                                subscriptions
                            );
                        }
                    }
                    if (entityField.atLeastOneRequiredRules) {
                        for (const rule of entityField.atLeastOneRequiredRules) {
                            this.setupAtLeastOneRequiredRule(
                                rule,
                                planningForm.form,
                                ['planning', 'planningData'],
                                planningForm.pheFields,
                                subscriptions
                            );
                        }
                    }
                }
            }
        });

        return planningForm;
    }

    private checkAndDisableFormBasedOnRoles(planningForm: PlanningFormData): void {
        if (planningForm.mainPhe?.orginal) {
            const entityType = environment.entitytypes[planningForm.mainPhe.orginal.entitytype_key];
            planningForm.mainPhe.disabledByRoles = !this.userCanEditPlanning(entityType, planningForm.orginal);
            if (planningForm.mainPhe.disabledByRoles) {
                planningForm.mainPhe.form.disable();
                planningForm.form.controls.planning.disable();
                planningForm.form.controls.planningData.disable();
                planningForm.form.disable();
            }
        }
        planningForm.pheFields.forEach(phe => {
            if (phe.orginal?.entitytype_key) {
                const entityType = environment.entitytypes[phe.orginal.entitytype_key];
                phe.disabledByRoles = !this.userCanEditPlanning(entityType, planningForm.orginal);
                if (phe.disabledByRoles) {
                    phe.form.disable();
                }
            }
        });
    }

    userCanEditPlanning(entityType: EntityType, planning?: Planning): boolean {
        // If may_edit_roles is defined and user has one of those roles, allow editing
        if (entityType.may_edit_roles && entityType.may_edit_roles.length > 0) {
            if (AuthorisationService.hasAnyRole(entityType.may_edit_roles)) {
                return true;
            }
        }

        // If may_edit_own_roles is defined, check if user has the role AND is the creator
        if (entityType.may_edit_own_roles && entityType.may_edit_own_roles.length > 0) {
            if (AuthorisationService.hasAnyRole(entityType.may_edit_own_roles)) {
                // Check if this is the user's own planning
                if (planning && planning.user_id === LocalStorage.user.id) {
                    return true;
                }
                // If no planning object (new planning), allow creation
                if (!planning) {
                    return true;
                }
            }
        }

        // If neither role array is defined or empty, allow editing (no restrictions)
        const hasNoEditRoles = !entityType.may_edit_roles || entityType.may_edit_roles.length === 0;
        const hasNoEditOwnRoles = !entityType.may_edit_own_roles || entityType.may_edit_own_roles.length === 0;
        return hasNoEditRoles && hasNoEditOwnRoles;
    }

    setupRule(
        control: FormControl,
        entityField: EntityField,
        rule: { field: string; hasValue: any[] },
        form: FormGroup,
        groups: string[],
        subscriptions: Subscription
    ) {
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }
        if (controllingControl) {
            subscriptions.add(
                controllingControl.valueChanges
                    .pipe(startWith(controllingControl.value), debounceTime(250))
                    .subscribe(value => {
                        const isRequired = rule.hasValue.includes(value);
                        const baseValidators = this.buildValidators(entityField);
                        if (isRequired) {
                            baseValidators.push(Validators.required);
                        }
                        control.setValidators(baseValidators);
                        control.updateValueAndValidity({ emitEvent: false });
                    })
            );
        }
    }

    setupDisableRule(
        rule: { field: string; hasValue: any[]; disableFields: string[] },
        planningForm: PlanningFormData,
        groups: string[],
        subscriptions: Subscription
    ) {
        const form = planningForm.form;
        const pheFields = planningForm.pheFields;

        // Find the controlling field
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }

        if (controllingControl) {
            // Subscribe to value changes
            subscriptions.add(
                controllingControl.valueChanges.pipe(debounceTime(250)).subscribe(value => {
                    const shouldDisable = rule.hasValue.includes(value);

                    // Disable/enable fields in planning and planningData
                    for (const fieldName of rule.disableFields) {
                        for (const group of groups) {
                            const targetControl = form.get(group)?.get(fieldName);
                            if (targetControl) {
                                if (shouldDisable) {
                                    targetControl.disable();
                                } else if (!planningForm.mainPhe.disabledByRoles) {
                                    targetControl.enable();
                                }
                            }
                        }

                        // Also check PHE fields
                        pheFields.forEach(phe => {
                            const pheControl = phe.form.get('phe')?.get(fieldName);
                            if (pheControl) {
                                if (shouldDisable) {
                                    pheControl.disable();
                                } else if (!phe.disabledByRoles) {
                                    pheControl.enable();
                                }
                            }
                            const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                            if (pheDataControl) {
                                if (shouldDisable) {
                                    pheDataControl.disable();
                                } else if (!phe.disabledByRoles) {
                                    pheDataControl.enable();
                                }
                            }
                        });
                    }
                })
            );

            // Trigger initial state
            const currentValue = controllingControl.value;
            const shouldDisable = rule.hasValue.includes(currentValue);
            for (const fieldName of rule.disableFields) {
                for (const group of groups) {
                    const targetControl = form.get(group)?.get(fieldName);
                    if (targetControl) {
                        if (shouldDisable) {
                            targetControl.disable();
                        } else if (!planningForm.mainPhe.disabledByRoles) {
                            targetControl.enable();
                        }
                    }
                }

                // Also check PHE fields for initial state
                pheFields.forEach(phe => {
                    const pheControl = phe.form.get('phe')?.get(fieldName);
                    if (pheControl) {
                        if (shouldDisable) {
                            pheControl.disable();
                        } else if (!phe.disabledByRoles) {
                            pheControl.enable();
                        }
                    }
                    const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                    if (pheDataControl) {
                        if (shouldDisable) {
                            pheDataControl.disable();
                        } else if (!phe.disabledByRoles) {
                            pheDataControl.enable();
                        }
                    }
                });
            }
        }
    }

    setupAtLeastOneRequiredRule(
        rule: { field: string; hasValue: any[]; requiredFields: string[] },
        form: FormGroup,
        groups: string[],
        pheFields: PheFormData[],
        subscriptions: Subscription
    ) {
        // Find the controlling field (e.g., status_key in planning)
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }

        if (!controllingControl) {
            return;
        }

        // Create a unique identifier for this validator to prevent duplicates
        const validatorKey = `atLeastOne_${rule.field}_${rule.requiredFields.join('_')}`;

        // Create the validator function
        const atLeastOneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
            const shouldValidate = rule.hasValue.includes(controllingControl.value);
            if (!shouldValidate) {
                return null; // No validation needed
            }

            // Check if at least one field has a value
            let hasValue = false;

            // Collect field labels for error message
            const fieldLabels: string[] = [];

            // Check in phe and pheData forms
            pheFields.forEach(phe => {
                for (const fieldName of rule.requiredFields) {
                    const pheControl = phe.form.get('phe')?.get(fieldName);
                    const pheDataControl = phe.form.get('pheData')?.get(fieldName);

                    // Get field label from EntityField
                    const pheField = phe.phe.get(fieldName);
                    const pheDataField = phe.pheData.get(fieldName);
                    const fieldLabel = pheField?.name || pheDataField?.name;

                    if (fieldLabel && !fieldLabels.includes(fieldLabel)) {
                        fieldLabels.push(fieldLabel);
                    }

                    if (pheControl?.value || pheDataControl?.value) {
                        hasValue = true;
                    }
                }
            });

            return hasValue ? null : { atLeastOneRequired: { fields: fieldLabels } };
        };

        // Store validator reference to avoid duplicates
        (atLeastOneValidator as any).__validatorKey = validatorKey;

        // Helper function to get EntityField for a control to rebuild base validators
        const getBaseValidators = (phe: PheFormData, group: string, fieldName: string): ValidatorFn[] => {
            const entityField = group === 'phe' ? phe.phe.get(fieldName) : phe.pheData.get(fieldName);
            return entityField ? this.buildValidators(entityField) : [];
        };

        // Apply validators to all fields in the rule
        pheFields.forEach(phe => {
            for (const fieldName of rule.requiredFields) {
                // Handle phe controls
                const pheControl = phe.form.get('phe')?.get(fieldName);
                if (pheControl) {
                    const baseValidators = getBaseValidators(phe, 'phe', fieldName);
                    // Check if this validator is not already added
                    const hasValidator = baseValidators.some((v: any) => v.__validatorKey === validatorKey);
                    if (!hasValidator) {
                        pheControl.setValidators([...baseValidators, atLeastOneValidator]);
                        pheControl.updateValueAndValidity({ emitEvent: false });
                    }
                }

                // Handle pheData controls
                const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                if (pheDataControl) {
                    const baseValidators = getBaseValidators(phe, 'pheData', fieldName);
                    // Check if this validator is not already added
                    const hasValidator = baseValidators.some((v: any) => v.__validatorKey === validatorKey);
                    if (!hasValidator) {
                        pheDataControl.setValidators([...baseValidators, atLeastOneValidator]);
                        pheDataControl.updateValueAndValidity({ emitEvent: false });
                    }
                }
            }
        });

        // Subscribe to controlling field changes
        subscriptions.add(
            controllingControl.valueChanges.subscribe(() => {
                pheFields.forEach(phe => {
                    for (const fieldName of rule.requiredFields) {
                        phe.form.get('phe')?.get(fieldName)?.updateValueAndValidity({ emitEvent: false });
                        phe.form.get('pheData')?.get(fieldName)?.updateValueAndValidity({ emitEvent: false });
                    }
                });
            })
        );

        // Subscribe to each field's changes to update all fields in the group
        pheFields.forEach(phe => {
            for (const fieldName of rule.requiredFields) {
                const pheControl = phe.form.get('phe')?.get(fieldName);
                if (pheControl) {
                    subscriptions.add(
                        pheControl.valueChanges.subscribe(() => {
                            // Update all other fields in the group
                            pheFields.forEach(p => {
                                for (const fn of rule.requiredFields) {
                                    p.form.get('phe')?.get(fn)?.updateValueAndValidity({ emitEvent: false });
                                    p.form.get('pheData')?.get(fn)?.updateValueAndValidity({ emitEvent: false });
                                }
                            });
                        })
                    );
                }

                const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                if (pheDataControl) {
                    subscriptions.add(
                        pheDataControl.valueChanges.subscribe(() => {
                            // Update all other fields in the group
                            pheFields.forEach(p => {
                                for (const fn of rule.requiredFields) {
                                    p.form.get('phe')?.get(fn)?.updateValueAndValidity({ emitEvent: false });
                                    p.form.get('pheData')?.get(fn)?.updateValueAndValidity({ emitEvent: false });
                                }
                            });
                        })
                    );
                }
            }
        });
    }
}

export interface PlanningFg {
    planning: FormGroup;
    planningData: FormGroup;
    planningHas: FormArray<
        FormGroup<{
            phe: FormGroup;
            pheData: FormGroup;
        }>
    >;
    mainPlanningHas: FormGroup<{
        phe: FormGroup;
        pheData: FormGroup;
    }>; //zit ook in planningHas
    employeePlanning: FormArray<FormGroup<EmployeePlanningFg>>;
    planningMaterials: FormArray;
}

export class PlanningFormData {
    orginal: Planning;
    form: FormGroup<PlanningFg>;
    planningFieldsMap: Map<string, Map<string, EntityFormField>>;
    pheFields: PheFormData[] = [];
    mainPhe: PheFormData;
    planningFiles: PlanningFile[] = [];
}

export class PheFormData {
    id: number;
    orginal: PlanningHasEntity;
    form: FormGroup<{
        phe: FormGroup;
        pheData: FormGroup;
    }>;
    phe: Map<string, EntityFormField>;
    pheData: Map<string, EntityFormField>;
    disabledByRoles: boolean = false;
}

export interface EmployeePlanningFg {
    id?: FormControl<number>;
    _moved_from_id?: FormControl<number>;
    afas_project_id?: FormControl<string>;
    dayOff?: FormControl<boolean>;
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    user_id: FormControl<number>;
    planning_id: FormControl<number>;
    origin?: FormControl<string>;
    destination?: FormControl<string>;
    work_begin?: FormControl<string>;
    work_end?: FormControl<string>;
    comment: FormControl<string>;
    created_at?: FormControl<Date>;
    updated_at?: FormControl<Date>;
    deleted_at?: FormControl<Date>;

    conflictText?: FormControl<string>;
    function?: FormControl<string>;
    employee?: FormControl<string>;
}

export interface EntityFormField extends EntityField {
    fc: FormControl;
}
